import React, { createContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { setAuth } from '../utils/auth';
import { notifySessionExpired } from '../utils/sessionExpiry';
import {
  ActionName,
  ActionStatus,
  fetchIssuer,
  mayBeReturn,
  parseActionReturn,
  trustedIssuers,
} from '../utils/actionReturn';

// Define the user type
interface User {
  id: string;
  username: string;
  email: string;
  tenantId: string | null;
  roles: string[];
}

// 'failed' means Keycloak initialisation itself failed (unreachable, token exchange refused); a
// gone session never gets here, because login-required sends it to the sign-in page.
export type AuthPhase = 'initializing' | 'ready' | 'failed';

// The result of adding a sign-in method (SF-167). 'app': an action this app started, reported by
// keycloak-js after its stored-state check, so it can be stated as fact. 'link': a manual-link
// return recognised in the query, which anyone can craft, so it must not be presented as verified.
export interface SignInActionOutcome {
  source: 'app' | 'link';
  action: ActionName | null;
  status: ActionStatus | null;
}

// A link outcome must outlive one page load: after a manual link keycloak-js holds no tokens, so
// login-required leaves for Keycloak and comes back (SSO, no prompt) before the app can show it.
// Same-origin and per-tab; it carries only the allowlisted, unverified link outcome.
const PENDING_LINK_OUTCOME = 'stratforth.signInActionLinkOutcome';
const PENDING_TTL_MS = 5 * 60 * 1000;

const takePendingLinkOutcome = (): SignInActionOutcome | null => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_LINK_OUTCOME);
    window.sessionStorage.removeItem(PENDING_LINK_OUTCOME);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    if (typeof pending?.at !== 'number' || Date.now() - pending.at > PENDING_TTL_MS) return null;
    const action = pending.action === 'CONFIGURE_TOTP' || pending.action === 'webauthn-register' ? pending.action : null;
    const status = pending.status === 'success' || pending.status === 'cancelled' || pending.status === 'error' ? pending.status : null;
    return { source: 'link', action, status };
  } catch {
    return null;
  }
};

const keepPendingLinkOutcome = (outcome: SignInActionOutcome) => {
  try {
    window.sessionStorage.setItem(PENDING_LINK_OUTCOME,
      JSON.stringify({ action: outcome.action, status: outcome.status, at: Date.now() }));
  } catch {
    // Storage unavailable: the outcome shows only if init completes on this load.
  }
};

const clearPendingLinkOutcome = () => {
  try {
    window.sessionStorage.removeItem(PENDING_LINK_OUTCOME);
  } catch {
    // Nothing to clear.
  }
};

// Auth context type
interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  phase: AuthPhase;
  actionOutcome: SignInActionOutcome | null;
  dismissActionOutcome: () => void;
  user: User | null;
  keycloak: Keycloak | null;
  login: (redirectUri?: string) => void;
  /** Starts a Keycloak required action and returns to the current page. */
  addSignInMethod: (action: ActionName) => void;
  logout: () => void;
  refresh: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
}

// Create auth context with default values
export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loading: true,
  phase: 'initializing',
  actionOutcome: null,
  dismissActionOutcome: () => {},
  user: null,
  keycloak: null,
  login: () => {},
  addSignInMethod: () => {},
  logout: () => {},
  refresh: async () => {},
  getToken: async () => undefined,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [phase, setPhase] = useState<AuthPhase>('initializing');
  const [actionOutcome, setActionOutcome] = useState<SignInActionOutcome | null>(null);
  // One start per page load, whatever re-renders or remounts happen (keycloak-js init is one-shot).
  const started = useRef(false);

  // Initialize Keycloak
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    let keycloakInstance: Keycloak | null = null;
    let linkOutcome: SignInActionOutcome | null = takePendingLinkOutcome();
    let appOutcome: SignInActionOutcome | null = null;
    const initKeycloak = async () => {
      try {
        const runtimeConfig = getRuntimeConfig();
        // Derive Keycloak base URL from current origin so the app works behind a reverse proxy.
        const keycloakUrl =
          runtimeConfig.keycloakUrl ||
          process.env.REACT_APP_KEYCLOAK_URL ||
          `${window.location.origin.replace(/\/$/, '')}/auth`;

        const keycloakRealm = runtimeConfig.keycloakRealm || process.env.REACT_APP_KEYCLOAK_REALM || 'stratforth';
        const keycloakClientId = runtimeConfig.keycloakClientId || process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'stratforth-frontend';

        // A manual required-action link returns in the query, which keycloak-js ignores: remove
        // it first, or login-required restarts with it in redirect_uri (SF-167, Q-328).
        // The configured issuer is tried first; discovery (bounded) only when it does not match,
        // as behind the Compose dashboard, which advertises the internal issuer.
        if (mayBeReturn(window.location.href)) {
          let returned = parseActionReturn(window.location.href, trustedIssuers(keycloakUrl, keycloakRealm));
          if (!returned.recognised) {
            const issuer = await fetchIssuer(keycloakUrl, keycloakRealm);
            if (issuer) returned = parseActionReturn(window.location.href, trustedIssuers(keycloakUrl, keycloakRealm, issuer));
          }
          if (returned.recognised) {
            window.history.replaceState(window.history.state, '', returned.cleanUrl);
            if (returned.kind === 'action') {
              linkOutcome = { source: 'link', action: returned.action, status: returned.status };
              keepPendingLinkOutcome(linkOutcome);
            }
          }
        }

        // Create Keycloak instance
        const kc = new Keycloak({
          url: keycloakUrl, // Keycloak server URL (proxied)
          realm: keycloakRealm, // Realm to use
          clientId: keycloakClientId, // Client ID
        });
        keycloakInstance = kc;

        // Fires while keycloak-js processes its own callback, after the stored state matched.
        kc.onActionUpdate = (status, action) => {
          const knownAction = action === 'CONFIGURE_TOTP' || action === 'webauthn-register' ? action : null;
          const knownStatus = status === 'success' || status === 'cancelled' || status === 'error' ? status : null;
          appOutcome = { source: 'app', action: knownAction, status: knownStatus };
        };

        // Initialize Keycloak
        const authenticated = await kc.init({
          onLoad: 'login-required', // Force login if not authenticated
          pkceMethod: 'S256',
          checkLoginIframe: false, // Disable iframe check to prevent redirect loops
          enableLogging: true, // Enable logging for debugging
          silentCheckSsoFallback: false, // Don't fallback to check-sso
          // Removed responseMode and flow as they might be causing issues with the latest version
        });

        setKeycloak(kc);
        setIsAuthenticated(authenticated);
        
        // Make keycloak available globally for debugging
        (window as any).keycloak = kc;

        // If not authenticated, redirect to login
        if (!authenticated) {
          kc.login({
            redirectUri: window.location.origin + '/',
          });
          return;
        }

        // If authenticated, set user information
        if (authenticated) {
          try {
            // Try to load user profile, but don't fail if it errors
            const userProfile = await kc.loadUserProfile().catch(() => null);
            
            // Get roles from token
            const roles = kc.realmAccess?.roles || [];
            
            // Get tenant ID from token claims
            let tenantId: string | null = null;

            // Extract canonical tenantId from token, falling back to dedicated-client runtime config.
            const tokenParsed = kc.tokenParsed as any;
            if (tokenParsed && typeof tokenParsed.tenantId === 'string') {
              tenantId = tokenParsed.tenantId;
            } else if (typeof runtimeConfig.tenantId === 'string' && runtimeConfig.tenantId.trim()) {
              tenantId = runtimeConfig.tenantId.trim();
            }
            
            // Use token parsed data if profile loading failed
            const username = userProfile?.username || tokenParsed?.preferred_username || tokenParsed?.sub || 'user';
            const email = userProfile?.email || tokenParsed?.email || '';
            const id = userProfile?.id || tokenParsed?.sub || '';
            
            setUser({
              id,
              username,
              email,
              tenantId,
              roles,
            });
          } catch (profileError) {
            console.warn('Could not load full user profile, using token data:', profileError);
            // Set minimal user data from token
            const tokenParsed = kc.tokenParsed as any;
            setUser({
              id: tokenParsed?.sub || '',
              username: tokenParsed?.preferred_username || 'user',
              email: tokenParsed?.email || '',
              tenantId:
                typeof tokenParsed?.tenantId === 'string'
                  ? tokenParsed.tenantId
                  : typeof runtimeConfig.tenantId === 'string' && runtimeConfig.tenantId.trim()
                    ? runtimeConfig.tenantId.trim()
                    : null,
              roles: kc.realmAccess?.roles || [],
            });
          }
        }

        // Set up token refresh
        if (authenticated) {
          kc.onTokenExpired = () => {
            kc.updateToken(30).catch(() => {
              // If token refresh fails, prompt the user to re-authenticate instead of
              // logging out silently mid-session (which would discard any unsaved edit).
              notifySessionExpired();
            });
          };
        }
        clearPendingLinkOutcome();
        setActionOutcome(appOutcome ?? linkOutcome);
        setPhase('ready');
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        // Show the failure with a "Sign in again" control (SignInFailed) instead of restarting the
        // login unasked, which could loop. Recovery is login() on this instance, never a second init.
        if (keycloakInstance) setKeycloak(keycloakInstance);
        clearPendingLinkOutcome();
        setActionOutcome(appOutcome ?? linkOutcome);
        setPhase('failed');
      } finally {
        setLoading(false);
      }
    };

    initKeycloak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login function
  const login = useCallback((redirectUri?: string) => {
    if (keycloak) {
      keycloak.login({
        redirectUri: redirectUri || window.location.origin + '/',
      });
    }
  }, [keycloak]);

  // The current URL is already free of any Keycloak return (cleaned before init), so the user comes
  // back to the page they started from.
  const addSignInMethod = useCallback((action: ActionName) => {
    if (keycloak) {
      keycloak.login({ action, redirectUri: window.location.href });
    }
  }, [keycloak]);

  const dismissActionOutcome = useCallback(() => setActionOutcome(null), []);

  // Logout function
  const logout = useCallback(() => {
    if (keycloak) {
      keycloak.logout();
    }
  }, [keycloak]);

  // Token refresh function
  const refresh = useCallback(async () => {
    if (keycloak) {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          console.log('Token refreshed');
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Prompt re-authentication rather than a silent logout mid-session.
        notifySessionExpired();
      }
    }
  }, [keycloak]);

  // Get current token
  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (keycloak) {
      try {
        await keycloak.updateToken(30);
        return keycloak.token;
      } catch (error) {
        console.error('Failed to get token:', error);
        return undefined;
      }
    }
    return undefined;
  }, [keycloak]);

  // Create the auth context value
  const authContextValue = useMemo(() => ({
    isAuthenticated,
    loading,
    phase,
    actionOutcome,
    dismissActionOutcome,
    user,
    keycloak,
    login,
    addSignInMethod,
    logout,
    refresh,
    getToken,
  }), [isAuthenticated, loading, phase, actionOutcome, dismissActionOutcome, user, keycloak, login,
    addSignInMethod, logout, refresh, getToken]);

  // Set the auth context for use in utils/api.ts
  useEffect(() => {
    if (!loading) {
      setAuth(authContextValue);
    }
  }, [loading, authContextValue]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
